import { PlusIcon } from '@heroicons/react/24/outline';
import { Button, LinearProgress } from '@mui/joy';

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { DeleteConfirmationButton } from '@/components/Delete';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/Table';
import {
  useDeleteTagKey,
  useDeleteTagValue,
  useGetTagValue,
} from '@/hooks/useSwr';
import SimpleEditIcon from '@/icons/SimpleEditIcon.svg?react';
import TagIcon from '@/icons/TagIcon.svg?react';
import {
  ResourceTag,
  ResourceTagKey,
  ResourceType,
  Tagging,
} from '@/types/schema';
import { cn } from '@/utils/tailwind';

import {
  ResourceTagCrudPane,
  TaggedResource,
} from './components/OldResourceTagCrudPane';
import { ResourceTagKeyCrudModal } from './components/ResourceTagKeyCrudModal';
import { isResourceTag, useListGroupedTags } from './utils/resourceTag.utils';

const ResourceTagsTable = ({
  resourceTags,
  onEdit,
}: {
  resourceTags: ResourceTag[];
  onEdit: (tag: ResourceTag) => void;
}) => {
  const deleteTagValue = useDeleteTagValue();

  const handleDelete = async (tag: ResourceTag) => {
    await deleteTagValue.trigger({
      templateUrlParams: {
        tagKeyId: tag.tag_key_id,
        tagValueId: tag.tag_value_id,
      },
    });
  };

  return (
    <Table className="table-fixed [&_tbody:last-child]:border-b-0">
      <TableHeader>
        <TableRow>
          <TableHead className="w-[15%] pl-5 align-middle">VALUE</TableHead>
          <TableHead className="w-[65%]">RESOURCES</TableHead>
          <TableHead className="w-[10%] pr-5 text-center"></TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {resourceTags.map((tag) => {
          const resources = tag.resources
            ? Object.entries(tag.resources).flatMap(
                ([resourceTypePlural, taggings]) => {
                  const visible = taggings.slice(0, 5);
                  const overflow = taggings.slice(5);
                  return [
                    ...visible.map((tagging: Tagging) => {
                      const resourceType = resourceTypePlural.slice(
                        0,
                        -1
                      ) as ResourceType;

                      if (resourceType === ResourceType.Dashboard) {
                        return null;
                      }

                      return (
                        <TaggedResource
                          resourceId={tagging.resource_id}
                          key={tagging.tagging_id}
                          resourceType={resourceType}
                          name={tagging.resource_name}
                          isLink={
                            ![
                              ResourceType.Experiment,
                              ResourceType.Dashboard,
                            ].includes(resourceType)
                          }
                        />
                      );
                    }),
                    overflow.length > 0 && (
                      <div
                        key={`${resourceTypePlural}-overflow`}
                        className="flex items-center gap-1 rounded-md border border-secondary px-2 py-1"
                      >
                        +{overflow.length} more {resourceTypePlural}
                      </div>
                    ),
                  ].filter(Boolean);
                }
              )
            : [];
          return (
            <TableRow key={`${tag.tag_key}-${tag.tag_value}`}>
              <TableCell className="w-[15%] pl-5 ">
                <div className="flex items-center gap-1 rounded-md border border-secondary bg-tertiary px-2 py-1">
                  <span className="truncate">{tag.tag_value}</span>
                </div>
              </TableCell>
              <TableCell className="w-[65%] gap-2 ">
                <div className="flex flex-wrap gap-2">
                  {resources.length > 0 ? resources : 'No resources'}
                </div>
              </TableCell>
              <TableCell className="w-[10%] pr-5">
                <div className="flex justify-end gap-4">
                  <SimpleEditIcon
                    className="h-4 w-4 cursor-pointer "
                    onClick={() => onEdit(tag)}
                  />
                  <DeleteConfirmationButton
                    onDelete={async () => await handleDelete(tag)}
                    title="Confirm delete tag"
                    description={
                      <span>Are you sure you want to delete this tag?</span>
                    }
                    deleteButtonText="Delete"
                    isDeleting={deleteTagValue.isMutating}
                  />
                </div>
              </TableCell>
            </TableRow>
          );
        })}
        {resourceTags.length === 0 && (
          <TableRow>
            <TableCell className="pl-5" colSpan={3}>
              No values
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};
const ResourceTagGroup = ({
  tagKeyId,
  tagKeyWithTaggings,
  onEditTagValue,
  onNewTagValue,
}: {
  tagKeyId: string;
  tagKeyWithTaggings: ResourceTagKey & { taggings: ResourceTag[] };
  onEditTagValue: (tag: ResourceTag) => void;
  onNewTagValue: (tagKeyId: string) => void;
}) => {
  const deleteTagKey = useDeleteTagKey();

  const handleDelete = async () => {
    await deleteTagKey.trigger({
      templateUrlParams: {
        tagKeyId,
      },
    });
  };

  return (
    <div className="rounded-lg border border-secondary">
      <div className="flex max-w-full items-center justify-between gap-6 border-b border-secondary px-5 py-4">
        <div className="flex flex-1 flex-col gap-1 overflow-hidden">
          <div className="flex items-center gap-1">
            <TagIcon className="h-4 w-4 flex-none" />
            <div className="flex flex-1 items-center gap-2 text-sm font-semibold">
              <span className="truncate">{tagKeyWithTaggings.key}</span>
            </div>
          </div>
          {tagKeyWithTaggings.description && (
            <div className="overflow-hidden text-ellipsis whitespace-nowrap text-sm text-tertiary">
              {tagKeyWithTaggings.description}
            </div>
          )}
        </div>
        <div className="flex flex-none items-center gap-4">
          <ResourceTagKeyCrudModal selectedKey={tagKeyWithTaggings}>
            {({ setOpen }) => (
              <button
                type="button"
                onClick={() => {
                  setOpen(true);
                }}
              >
                <SimpleEditIcon className="h-4 w-4 cursor-pointer" />
              </button>
            )}
          </ResourceTagKeyCrudModal>
          <DeleteConfirmationButton
            onDelete={handleDelete}
            title="Confirm delete tag key"
            description={
              <span>
                Are you sure you want to delete this tag key<br></br>
                and all tags associated with it?
              </span>
            }
            deleteButtonText="Delete"
            isDeleting={deleteTagKey.isMutating}
          />

          <Button
            variant="outlined"
            color="neutral"
            onClick={() => {
              onNewTagValue(tagKeyId);
            }}
            className="flex items-center gap-1"
            size="sm"
          >
            <PlusIcon className="h-4 w-4 flex-none" />
            <div className="flex-1 whitespace-nowrap">Value</div>
          </Button>
        </div>
      </div>
      <ResourceTagsTable
        resourceTags={tagKeyWithTaggings.taggings}
        onEdit={onEditTagValue}
      />
    </div>
  );
};

const OrganizationResourceTags = () => {
  const { data: groupByTagKey, isLoading: resourceTagsLoading } =
    useListGroupedTags();
  const [selectedTag, setSelectedTag] = useState<
    ResourceTag | { tag_key: string; tag_key_id: string } | undefined
  >(undefined);

  const { data: selectedTagValue } = useGetTagValue(
    selectedTag?.tag_key_id,
    isResourceTag(selectedTag) ? selectedTag?.tag_value_id : undefined
  );
  const [isOpen, setOpen] = useState(false);

  const noTags = Object.keys(groupByTagKey).length === 0;
  if (resourceTagsLoading) {
    return (
      <div className="h-full">
        <LinearProgress />
      </div>
    );
  }
  return (
    <div className="h-full overflow-y-auto">
      <div className="flex justify-between gap-6">
        <div className="flex flex-grow flex-col gap-1">
          <h3 className={cn('font-semibold', 'text-lg')}>Resource tags</h3>
          <div className="text-sm text-tertiary">
            Resource tags are used to organize resources such as traces,
            datasets, and more in your workspace
          </div>
        </div>
        {!noTags && (
          <div className="flex items-center gap-2">
            <Button
              onClick={() => {
                setSelectedTag(undefined);
                setOpen(true);
              }}
              className="flex items-center gap-1"
              size="sm"
            >
              <TagIcon className="h-4 w-4 flex-none" />
              <div className="flex-1 whitespace-nowrap">New Tag</div>
            </Button>
            <Link
              to="https://docs.langchain.com/langsmith/set-up-resource-tags"
              target="_blank"
            >
              <Button variant="outlined" color="neutral" size="sm">
                Docs
              </Button>
            </Link>
          </div>
        )}
        <ResourceTagCrudPane
          resourceTag={
            selectedTag && {
              ...selectedTag,
              description: selectedTagValue?.description,
            }
          }
          isOpen={isOpen}
          setOpen={setOpen}
        ></ResourceTagCrudPane>
      </div>

      <div className="mt-4 flex flex-col gap-4">
        {noTags && (
          <div className="flex h-[250px] flex-col items-center justify-center gap-2 px-12 py-6">
            <div className="mt-2 rounded-lg border border-secondary p-2">
              <TagIcon className="h-6 w-6 flex-shrink-0 text-tertiary" />
            </div>
            <div className=" flex items-center gap-2 font-semibold">
              Create your first resource tag
            </div>
            <div className="w-[400px] text-center text-sm">
              Resource tags are used to organize resources such as traces,
              datasets, and more in your workspace
            </div>
            <div className="mt-4 flex justify-center gap-2">
              <Button
                size="sm"
                startDecorator={<PlusIcon className="h-5 w-5" />}
                onClick={() => {
                  setSelectedTag(undefined);
                  setOpen(true);
                }}
              >
                New tag
              </Button>
              <Link
                to="https://docs.langchain.com/langsmith/set-up-resource-tags"
                target="_blank"
              >
                <Button size="sm" variant="outlined" color="neutral">
                  Docs
                </Button>
              </Link>
            </div>
          </div>
        )}
        {groupByTagKey &&
          Object.entries(groupByTagKey).map(
            ([tagKeyId, tagKeyWithTaggings]) => {
              return (
                <ResourceTagGroup
                  key={tagKeyId}
                  tagKeyId={tagKeyId}
                  tagKeyWithTaggings={tagKeyWithTaggings}
                  onEditTagValue={(tag) => {
                    setSelectedTag(tag);
                    setOpen(true);
                  }}
                  onNewTagValue={() => {
                    setSelectedTag({
                      tag_key_id: tagKeyId,
                      tag_key: tagKeyWithTaggings.key,
                    });
                    setOpen(true);
                  }}
                />
              );
            }
          )}
      </div>
    </div>
  );
};

export { OrganizationResourceTags };
